import React from 'react';
import { CheckCheck, FileCheck2, Loader2, Plus } from 'lucide-react';
import { MERKMAL_LABEL } from '../planungsphase/fragenkatalog';
import { bedingungsHinweis } from './unterlagenErgaenzen';
import type { BedingterDokumentVorschlag } from './uebernahme';

// Bedingte mitgeltende Dokumente, deren Bedingung die Planungsphase bejaht
// (Merkmal ausdrücklich „ja"). Bis Oktober 2026 blieben sie bei jeder
// automatischen Übernahme draußen und waren nur im Editor zu finden. Hier
// stehen sie als VORSCHLAG — übernommen wird je Dokument oder gesammelt per
// Klick, nie beim Laden (Hausregel „Vorschläge mit Bedingung nie automatisch").

interface Props {
  vorschlaege: readonly BedingterDokumentVorschlag[];
  busy: boolean;
  onUebernehmen: (liste: readonly BedingterDokumentVorschlag[]) => void;
}

const merkmalText = (merkmale: readonly string[]): string =>
  merkmale.map(k => MERKMAL_LABEL[k] || k).join(' · ');

export const BedingteDokumente: React.FC<Props> = ({ vorschlaege, busy, onUebernehmen }) => {
  if (vorschlaege.length === 0) return null;

  // Nach Arbeitsschritt gebündelt, Reihenfolge der Schritte.
  const gruppen: { schrittId: string; bezeichnung: string; eintraege: BedingterDokumentVorschlag[] }[] = [];
  vorschlaege.forEach(v => {
    const g = gruppen.find(x => x.schrittId === v.schrittId);
    if (g) g.eintraege.push(v);
    else gruppen.push({ schrittId: v.schrittId, bezeichnung: v.bezeichnung, eintraege: [v] });
  });

  const knopf = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border '
    + 'disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <section className="bg-emerald-50 border border-emerald-200 rounded-xl">
      <div className="flex items-center gap-2 px-4 py-2.5">
        <FileCheck2 className="w-4 h-4 text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-emerald-900 truncate">
            Mitgeltende Dokumente nach den Merkmalen der Planungsphase ({vorschlaege.length})
          </div>
          <div className="text-[11px] text-emerald-800 leading-snug">
            Der Katalog schlägt diese Unterlagen nur unter einer Bedingung vor; die Planungsphase bejaht das
            zugehörige Merkmal. Übernommen wird erst per Klick, an den jeweiligen Arbeitsschritt.
          </div>
        </div>
        {vorschlaege.length > 1 && (
          <button
            type="button"
            onClick={() => onUebernehmen(vorschlaege)}
            disabled={busy}
            title="Alle aufgeführten Dokumente an ihre Arbeitsschritte hängen"
            className={`${knopf} text-white bg-emerald-600 border-emerald-600 hover:bg-emerald-700 shrink-0`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
            Alle übernehmen ({vorschlaege.length})
          </button>
        )}
      </div>
      <ul className="border-t border-emerald-200 divide-y divide-emerald-100">
        {gruppen.map(g => (
          <li key={g.schrittId} className="px-4 py-2.5">
            <div className="text-xs font-bold text-slate-800 truncate" title={g.bezeichnung}>{g.bezeichnung}</div>
            <ul className="mt-1 space-y-1.5">
              {g.eintraege.map(v => (
                <li key={v.dokument.id} className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-800">
                      <span className="font-bold text-emerald-800">{v.dokument.name}</span>
                      {v.dokument.kurz && <span className="text-slate-400"> ({v.dokument.kurz})</span>}
                    </div>
                    {v.bedingungen.map(b => (
                      <div key={b} className="text-[11px] text-slate-500 leading-snug">{bedingungsHinweis(b)}</div>
                    ))}
                    <div className="text-[11px] text-emerald-700 leading-snug">
                      <span className="font-bold">Merkmal bejaht:</span> {merkmalText(v.merkmale)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUebernehmen([v])}
                    disabled={busy}
                    title="Dieses Dokument an den Arbeitsschritt hängen"
                    className={`${knopf} text-emerald-700 bg-white border-emerald-300 hover:bg-emerald-100 shrink-0`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Übernehmen
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
};
